import type { GameStore } from '../simulation/GameStore';
import { ParkingLotEngine } from '../simulation/ParkingLotEngine';
import { formatCurrency, formatCurrencyCompact } from '../utils/formatCurrency';
import { getLang } from './i18n';

/**
 * Finance & parking dashboard (spec sections 23 & 39): a toggled HTML overlay
 * showing company finances, per-branch stats, and a 30-day profit chart. Pure
 * view over the GameStore.
 */
export class Dashboard {
  private readonly root: HTMLElement;
  private readonly store: GameStore;
  private panel?: HTMLElement;

  constructor(root: HTMLElement, store: GameStore) {
    this.root = root;
    this.store = store;
  }

  isOpen(): boolean {
    return !!this.panel;
  }

  toggle(): void {
    if (this.panel) {
      this.close();
    } else {
      this.open();
    }
  }

  close(): void {
    this.panel?.remove();
    this.panel = undefined;
  }

  destroy(): void {
    this.close();
  }

  private th(): boolean {
    return getLang() === 'th';
  }

  private open(): void {
    this.injectStyles();
    const wrap = document.createElement('div');
    wrap.className = 'pt-dash-backdrop ui-interactive';
    wrap.addEventListener('click', (e) => {
      if (e.target === wrap) this.close();
    });

    const panel = document.createElement('div');
    panel.className = 'pt-dash';
    panel.innerHTML = this.buildHtml();
    wrap.appendChild(panel);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'pt-dash-close';
    closeBtn.textContent = '✕';
    closeBtn.addEventListener('click', () => this.close());
    panel.appendChild(closeBtn);

    this.root.appendChild(wrap);
    this.panel = wrap;
  }

  private buildHtml(): string {
    const th = this.th();
    const state = this.store.getState();
    const fin = this.store.getFinance();
    const val = this.store.getValuation();
    const lots = this.store.getLots();
    const sharePct = Math.round(this.store.getPlayerMarketShare() * 100);

    // Month-to-date (last up to 30 settled days).
    const monthRev = fin.history.reduce((s, r) => s + r.revenue, 0);
    const monthExp = fin.history.reduce((s, r) => s + r.expense, 0);
    const monthProfit = monthRev - monthExp;
    const last = fin.history[fin.history.length - 1];

    let totalCap = 0;
    let totalOcc = 0;
    for (const l of lots) {
      totalCap += ParkingLotEngine.getCapacity(l);
      totalOcc += l.occupiedSpaces;
    }
    const occPct = totalCap > 0 ? Math.round((totalOcc / totalCap) * 100) : 0;

    const t = (en: string, thStr: string) => (th ? thStr : en);

    const stat = (label: string, value: string, color = '#eceff1') =>
      `<div class="pt-dash-stat"><span>${label}</span><b style="color:${color}">${value}</b></div>`;

    const cards = [
      stat(t('Cash', 'เงินสด'), formatCurrency(state.player.cash), '#ffd54f'),
      stat(t('Company value', 'มูลค่าบริษัท'), formatCurrencyCompact(val.total), '#80cbc4'),
      stat(t('Market share', 'ส่วนแบ่งตลาด'), `${sharePct}%`),
      stat(t('Revenue today', 'รายได้วันนี้'), formatCurrency(fin.todayRevenue), '#a5d6a7'),
      stat(
        t('Last day P/L', 'กำไร/ขาดทุนล่าสุด'),
        last ? formatCurrency(last.profit) : '—',
        last && last.profit >= 0 ? '#a5d6a7' : '#ef9a9a'
      ),
      stat(t('Occupancy', 'อัตราใช้งาน'), `${occPct}%`),
      stat(t('Month revenue', 'รายได้เดือนนี้'), formatCurrency(monthRev), '#a5d6a7'),
      stat(t('Month expense', 'รายจ่ายเดือนนี้'), formatCurrency(monthExp), '#ef9a9a'),
      stat(
        t('Month profit', 'กำไรเดือนนี้'),
        formatCurrency(monthProfit),
        monthProfit >= 0 ? '#a5d6a7' : '#ef9a9a'
      ),
    ].join('');

    // Per-branch table.
    const rows = lots
      .map((l) => {
        const cap = ParkingLotEngine.getCapacity(l);
        return `<tr>
          <td>${l.name}</td>
          <td>${l.occupiedSpaces}/${cap}</td>
          <td>฿${l.parkingPrice}</td>
          <td>${Math.round(l.reputation)}</td>
          <td>${l.vehiclesServed}</td>
          <td>${l.vehiclesRejected}</td>
          <td style="color:${l.profit >= 0 ? '#a5d6a7' : '#ef9a9a'}">${formatCurrencyCompact(l.profit)}</td>
        </tr>`;
      })
      .join('');
    const table = lots.length
      ? `<table class="pt-dash-table">
          <thead><tr>
            <th>${t('Branch', 'สาขา')}</th><th>${t('Occ', 'ใช้งาน')}</th>
            <th>${t('Price', 'ราคา')}</th><th>${t('Rep', 'ชื่อเสียง')}</th>
            <th>${t('Served', 'ให้บริการ')}</th><th>${t('Rej', 'ปฏิเสธ')}</th>
            <th>${t('P/L', 'กำไร')}</th>
          </tr></thead><tbody>${rows}</tbody></table>`
      : `<p class="pt-dash-empty">${t('No lots yet.', 'ยังไม่มีลานจอด')}</p>`;

    // 30-day profit chart (bars).
    const chart = this.buildChart(fin.history.map((r) => r.profit));

    return `
      <h2>${t('📊 Dashboard', '📊 แดชบอร์ด')}</h2>
      <div class="pt-dash-grid">${cards}</div>
      <h3>${t('Branches', 'สาขาทั้งหมด')}</h3>
      ${table}
      <h3>${t('Profit — last 30 days', 'กำไร — 30 วันล่าสุด')}</h3>
      ${chart}
    `;
  }

  private buildChart(profits: number[]): string {
    if (profits.length === 0) {
      return `<p class="pt-dash-empty">${this.th() ? 'ยังไม่มีข้อมูล' : 'No data yet.'}</p>`;
    }
    const max = Math.max(1, ...profits.map((p) => Math.abs(p)));
    const bars = profits
      .map((p) => {
        const h = Math.round((Math.abs(p) / max) * 60);
        const pos = p >= 0;
        return `<div class="pt-bar ${pos ? 'pos' : 'neg'}" style="height:${h}px" title="${Math.round(p)}"></div>`;
      })
      .join('');
    return `<div class="pt-dash-chart">${bars}</div>`;
  }

  private injectStyles(): void {
    const ID = 'pt-dash-styles';
    if (document.getElementById(ID)) return;
    const style = document.createElement('style');
    style.id = ID;
    style.textContent = `
      .pt-dash-backdrop { position:absolute; inset:0; background:rgba(0,0,0,0.55);
        display:flex; align-items:center; justify-content:center; z-index:30; }
      .pt-dash { position:relative; width:min(860px,92vw); max-height:88vh; overflow:auto;
        background:#141c2b; border:1px solid #5c6bc0; border-radius:12px; padding:20px 22px;
        color:#eceff1; font-family:'Segoe UI',Tahoma,sans-serif; box-shadow:0 10px 40px rgba(0,0,0,0.5); }
      .pt-dash h2 { margin:0 0 12px; font-size:22px; color:#ffd54f; }
      .pt-dash h3 { margin:18px 0 8px; font-size:15px; color:#90a4ae; }
      .pt-dash-close { position:absolute; top:12px; right:14px; background:#263238; color:#eceff1;
        border:1px solid #455a64; border-radius:6px; width:32px; height:32px; cursor:pointer; font-size:16px; }
      .pt-dash-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
      .pt-dash-stat { background:#1a2333; border:1px solid #2b3648; border-radius:8px; padding:10px 12px;
        display:flex; flex-direction:column; gap:4px; }
      .pt-dash-stat span { font-size:12px; color:#90a4ae; }
      .pt-dash-stat b { font-size:18px; }
      .pt-dash-table { width:100%; border-collapse:collapse; font-size:13px; }
      .pt-dash-table th, .pt-dash-table td { text-align:left; padding:6px 8px; border-bottom:1px solid #2b3648; }
      .pt-dash-table th { color:#90a4ae; font-weight:600; }
      .pt-dash-empty { color:#90a4ae; font-size:13px; }
      .pt-dash-chart { display:flex; align-items:flex-end; gap:3px; height:70px; padding-top:8px;
        border-bottom:1px solid #2b3648; }
      .pt-bar { flex:1; min-width:3px; border-radius:2px 2px 0 0; }
      .pt-bar.pos { background:#66bb6a; }
      .pt-bar.neg { background:#ef5350; }
    `;
    document.head.appendChild(style);
  }
}
